import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { KeyRound, Save } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current: "", next: "" });
  const [saving, setSaving] = useState(false);

  const changePw = async () => {
    if (!pw.current || pw.next.length < 8) { toast.error("Passwords must be 8+ chars"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "" });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="profile-page">
      <PageHeader title="Your profile" subtitle="Account & security" />

      <Card className="card-soft border-transparent">
        <CardHeader><CardTitle className="font-display text-xl">Account</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center text-2xl font-semibold">
              {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="text-xl font-display">{user?.name || "—"}</div>
              <div className="text-sm text-muted-foreground">{user?.email}</div>
              <div className="mt-1"><Badge variant="secondary">{user?.role}</Badge></div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-soft border-transparent mt-4">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="font-display text-xl flex items-center gap-2"><KeyRound className="w-5 h-5" /> Change password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" /></div>
            <div><Label>New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-next" /></div>
          </div>
          <div>
            <Button onClick={changePw} disabled={saving} className="btn-primary-glow" data-testid="save-pw-btn"><Save className="w-4 h-4 mr-1" />Update password</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
